import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { IdCard, Search, ExternalLink, Trash2, Eye, LayoutGrid } from "lucide-react";

export default function AdminCartes() {
  const [tab, setTab] = useState("cards");
  return (
    <div data-testid="admin-cartes-page">
      <h1 className="font-heading text-2xl md:text-3xl font-bold flex items-center gap-2 mb-1"><IdCard className="text-[#E05638]" /> Cartes digitales</h1>
      <p className="text-slate-500 text-xs mb-5">Modérez les cartes des membres et gérez les modèles disponibles dans le studio.</p>
      <div className="inline-flex rounded-xl bg-white border border-slate-200 p-1 gap-1 mb-5">
        <button onClick={() => setTab("cards")} data-testid="admin-cartes-tab-cards" className={`px-4 py-1.5 rounded-lg text-sm font-semibold ${tab === "cards" ? "bg-[#E05638] text-white" : "text-slate-600 hover:bg-slate-100"}`}>Cartes</button>
        <button onClick={() => setTab("templates")} data-testid="admin-cartes-tab-templates" className={`px-4 py-1.5 rounded-lg text-sm font-semibold ${tab === "templates" ? "bg-[#E05638] text-white" : "text-slate-600 hover:bg-slate-100"}`}>Modèles</button>
      </div>
      {tab === "cards" ? <CardsTab /> : <TemplatesTab />}
    </div>
  );
}

function CardsTab() {
  const [ov, setOv] = useState(null);
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState({});
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [ctype, setCtype] = useState("all");

  const loadList = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (status !== "all") params.set("status", status);
      if (ctype !== "all") params.set("card_type", ctype);
      const { data } = await api.get(`/cartes/admin/list?${params.toString()}`);
      setItems(data.items || []); setTypes(data.card_types || {});
    } catch { toast.error("Erreur de chargement."); }
  }, [q, status, ctype]);

  useEffect(() => { api.get("/cartes/admin/overview").then(({ data }) => setOv(data)).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(loadList, 300); return () => clearTimeout(t); }, [loadList]);

  const moderate = async (c, disabled) => {
    setItems((p) => p.map((x) => x.id === c.id ? { ...x, admin_disabled: disabled } : x));
    try { await api.post(`/cartes/admin/${c.id}/moderate`, { disabled }); toast.success(disabled ? "Carte masquée." : "Carte réactivée."); }
    catch { toast.error("Échec."); loadList(); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Supprimer définitivement la carte « ${c.title} » ?`)) return;
    try { await api.delete(`/cartes/admin/${c.id}`); setItems((p) => p.filter((x) => x.id !== c.id)); toast.success("Carte supprimée."); }
    catch { toast.error("Échec de la suppression."); }
  };

  const stats = ov ? [["Total", ov.total], ["Publiées", ov.published], ["Masquées", ov.disabled], ["Vues cumulées", ov.views]] : [];

  return (
    <div data-testid="admin-cartes-cards">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        {stats.map(([l, v]) => <div key={l} className="bg-white border border-slate-200 rounded-2xl p-4"><div className="text-2xl font-extrabold text-slate-900">{v}</div><div className="text-xs text-slate-500">{l}</div></div>)}
      </div>
      <div className="flex flex-wrap gap-2 mb-3">
        <div className="relative"><Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, e-mail, lien…" className="pl-8 w-56" data-testid="admin-cartes-search" /></div>
        <Select value={status} onValueChange={setStatus}><SelectTrigger className="w-40" data-testid="admin-cartes-status"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Tous les statuts</SelectItem><SelectItem value="published">Publiées</SelectItem><SelectItem value="draft">Brouillons</SelectItem></SelectContent></Select>
        <Select value={ctype} onValueChange={setCtype}><SelectTrigger className="w-44"><SelectValue placeholder="Type" /></SelectTrigger><SelectContent><SelectItem value="all">Tous les types</SelectItem>{Object.entries(types).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent></Select>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100" data-testid="admin-cartes-list">
        {items.length === 0 ? <div className="p-6 text-sm text-slate-400 text-center">Aucune carte.</div> :
          items.map((c) => (
            <div key={c.id} className="flex items-center gap-3 px-4 py-3 flex-wrap" data-testid={`admin-carte-${c.id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-slate-900 truncate flex items-center gap-2">
                  {c.title}
                  <Badge className={c.status === "published" ? "bg-emerald-500" : "bg-slate-400"}>{c.status === "published" ? "Publiée" : "Brouillon"}</Badge>
                  {c.admin_disabled && <Badge className="bg-rose-500">Masquée</Badge>}
                </div>
                <div className="text-xs text-slate-400 truncate">{c.owner_email} · /carte/{c.slug} · {types[c.card_type]?.label || c.card_type}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {c.status === "published" && !c.admin_disabled && <a href={c.public_url} target="_blank" rel="noreferrer"><Button size="sm" variant="outline"><Eye size={14} /></Button></a>}
                <label className="flex items-center gap-1.5 text-xs text-slate-500">Visible<Switch checked={!c.admin_disabled} onCheckedChange={(v) => moderate(c, !v)} data-testid={`admin-carte-toggle-${c.id}`} /></label>
                <Button size="sm" variant="outline" className="text-rose-600" onClick={() => remove(c)} data-testid={`admin-carte-del-${c.id}`}><Trash2 size={14} /></Button>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}

function TemplatesTab() {
  const [items, setItems] = useState([]);
  const [cols, setCols] = useState({});
  const load = useCallback(async () => {
    try { const { data } = await api.get("/cartes/admin/templates"); setItems(data.items || []); setCols(data.collections || {}); }
    catch { toast.error("Erreur de chargement."); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (t, disabled) => {
    setItems((p) => p.map((x) => x.id === t.id ? { ...x, disabled } : x));
    try { await api.post(`/cartes/admin/templates/${t.id}/toggle`, { disabled }); toast.success(disabled ? "Modèle désactivé." : "Modèle activé."); }
    catch { toast.error("Échec."); load(); }
  };

  return (
    <div data-testid="admin-cartes-templates" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {items.map((t) => (
        <div key={t.id} className={`border rounded-2xl overflow-hidden bg-white ${t.disabled ? "opacity-60 border-slate-200" : "border-slate-200"}`} data-testid={`admin-tpl-${t.id}`}>
          <div className="h-16 flex" >
            <span className="flex-1" style={{ background: t.colors?.bg }} /><span className="flex-1" style={{ background: t.colors?.primary }} /><span className="flex-1" style={{ background: t.colors?.secondary }} />
          </div>
          <div className="p-3 flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="font-semibold text-sm text-slate-900 truncate">{t.name}</div>
              <div className="text-[11px] text-slate-400">{cols[t.collection]} · {t.usage} utilisation(s)</div>
            </div>
            <Switch checked={!t.disabled} onCheckedChange={(v) => toggle(t, !v)} data-testid={`admin-tpl-toggle-${t.id}`} />
          </div>
        </div>
      ))}
    </div>
  );
}
